import {
  Box,
  Divider,
  Flex,
  Stack,
  Text,
  ThemeIcon,
  UnstyledButton,
} from '@mantine/core';
import {IconChevronRight, IconFile} from '@tabler/icons-react';
import React from 'react';
import type {PillProps} from 'src/design/atoms/Pill';
import {Pill} from 'src/design/atoms/Pill';
import {isValidArray} from 'src/utilities/objectUtils';

import classes from './DataListRow.module.css';

/**
 * Row renderer for `DataList`: an icon, primary/secondary text, optional
 * pills, and slots for extra nodes before/after/below each row.
 * @registry List row with icon + primary/secondary text + pills + chevron.
 *
 * Ported from `library/tables/DataListRow`. Prop surface kept identical.
 */

export interface DataListRowProps {
  data: any;
  meta: any;

  rowIcon?: React.ReactNode;

  onRowClick: (id: number, item: any) => void;
  /** Rows this returns true for render inert: no click target, no chevron. */
  onIsRowDisabled?: (item: any) => boolean;
  onRowTextRender?: (data: any, meta: any) => string;
  onRowSubTextRender?: (data: any, meta: any) => string;
  onGetPillMapping?: (item: any) => PillProps[];
  onGetRowIcon?: (item: any) => React.ReactNode;
  onRenderBeforeRowNode?: (item: any) => React.ReactNode;
  onRenderAfterRowNode?: (item: any) => React.ReactNode;
  onRenderDetailRowNode?: (data: any) => React.ReactNode;

  hasSearchTerm?: boolean;
}

export const DataListRow = ({
  data,
  meta,
  rowIcon,
  hasSearchTerm,
  onRowClick,
  onIsRowDisabled,
  onGetPillMapping,
  onGetRowIcon,
  onRowTextRender,
  onRowSubTextRender,
  onRenderBeforeRowNode,
  onRenderAfterRowNode,
  onRenderDetailRowNode,
}: DataListRowProps) => {
  if (hasSearchTerm && !isValidArray(data)) {
    return (
      <Text c="dimmed" ta="center" py="lg">
        No items match your search terms
      </Text>
    );
  }

  const renderRowContent = (item: any, isDisabled: boolean) => (
    <Flex gap="sm" align="center">
      <ThemeIcon size="md" variant="light" color="gray">
        {onGetRowIcon !== undefined ? (
          onGetRowIcon(item)
        ) : rowIcon !== undefined ? (
          rowIcon
        ) : (
          <IconFile size={18} />
        )}
      </ThemeIcon>
      <Box style={{flex: 1, minWidth: 0}}>
        <Flex align="center" gap="sm" wrap="wrap">
          <Text size="sm" fw={500} lineClamp={1} className={classes.rowTitle}>
            {onRowTextRender === undefined
              ? item[meta?.label]
              : onRowTextRender(item, meta)}
          </Text>
          {onGetPillMapping && (
            <Flex gap={4} wrap="wrap">
              {onGetPillMapping(item).map(pill => (
                <Pill key={pill.text} {...pill} />
              ))}
            </Flex>
          )}
        </Flex>
        {(onRowSubTextRender || item.internalIdentifier) && (
          <Text size="xs" c="dimmed" mt={2}>
            {onRowSubTextRender === undefined
              ? item.internalIdentifier
              : onRowSubTextRender(item, meta)}
          </Text>
        )}
      </Box>
      {!isDisabled && (
        <IconChevronRight size={16} color="var(--mantine-color-gray-4)" />
      )}
    </Flex>
  );

  return (
    <Stack gap={0}>
      {data.map((item: any, index: number) => {
        const isDisabled = onIsRowDisabled?.(item) === true;
        return (
          <React.Fragment key={item.id}>
            <Box>
              <Flex align="center" gap="sm">
                {onRenderBeforeRowNode && (
                  <Box>{onRenderBeforeRowNode(item)}</Box>
                )}
                {isDisabled ? (
                  <Box py={3} px="xs" style={{flex: 1, minWidth: 0}}>
                    {renderRowContent(item, true)}
                  </Box>
                ) : (
                  <UnstyledButton
                    onClick={() => onRowClick(item.id, item)}
                    py={3}
                    px="xs"
                    style={{flex: 1, minWidth: 0}}>
                    {renderRowContent(item, false)}
                  </UnstyledButton>
                )}
                {onRenderAfterRowNode && (
                  <Box ml="auto">{onRenderAfterRowNode(item)}</Box>
                )}
              </Flex>
              {onRenderDetailRowNode && (
                <Box ml="xl" pl="md">
                  {onRenderDetailRowNode(item)}
                </Box>
              )}
            </Box>
            {index < data.length - 1 && <Divider />}
          </React.Fragment>
        );
      })}
    </Stack>
  );
};
